<template>
  <div
    ref="container"
    :class="$style.googleAd"
  />
</template>

<script setup lang="ts">
  import { computed, onMounted, onUnmounted, useTemplateRef } from 'vue';
  import { useHead, useNuxtApp } from 'nuxt/app';
  import { PUSH_VIEW_BANNER } from '~~/common/plugins/aw-analytics';
  import type { PromotionSchema } from '~~/common/utils/AnalyticsBannerInteraction';
  import { AnalyticsBannerInteraction } from '~~/common/utils/AnalyticsBannerInteraction';
  import { parseHeadEntriesOnce, sanitizeBodyCode } from '~~/common/utils/parseHeadEntries';
  import { notifyAwGamMount, notifyAwGamUnmount } from '~~/common/utils/awGamSpaRefresh';
  // Flag-gated GAM debug logging (window.__gamDebug / ?gamDebug=1). See gamDebugLog.ts.
  import { gamDebugLog } from '~~/common/utils/gamDebugLog';

  interface GoogleAdContent {
    id?: string | number;
    contentId?: string | number;
    headCode?: string;
    bodyCode?: string;
    desktopBannerHeight?: number;
    mobileBannerHeight?: number;
    campaignName?: string;
  }

  const props = defineProps<{
    content: GoogleAdContent;
    // Part of the shared CMS content-component interface (AwCmsPage binds
    // :content-id on every <component :is>). Unused here — google_ad payloads
    // key off content.id — but declared so it is absorbed as a prop instead of
    // falling through onto the root <div> as an attribute.
    contentId?: string | number;
    analyticsBannerInteraction?: PromotionSchema | null;
    analyticsMeasurable?: boolean;
  }>();

  // Identifies which ad component (slot) a log line belongs to, mirroring the
  // head key prefix.
  function gamDebugKey (): string {
    return props.content.id != null ? `gam-${props.content.id}` : 'gam';
  }

  // Head entries (library loader script) are injected through useHead as soon as
  // a headCode is present. Marketing-consent gating is intentionally NOT done
  // here: the agency loader script handles consent itself, so the FE always
  // injects and lets the script decide whether to request ads.
  const headEntries = computed(() => {
    if (!props.content.headCode) {
      return {};
    }
    // Stable per-component key prefix → unhead dedupes the same entries across
    // SSR→CSR hydration and remounts instead of re-injecting them.
    // The CMS component's own `id` is the only always-present unique identifier
    // (`contentId` is absent on google_ad payloads), so it drives the prefix.
    const keyPrefix = props.content.id != null ? `gam-${props.content.id}` : 'gam';
    // parseHeadEntriesOnce additionally guards against re-injection across SPA
    // navigation within the same window: the inline defineSlot script of the
    // DIRECT pattern must not run again on B→A (it would re-define an already
    // registered slot). The stable key alone can't catch that — the head
    // context is torn down on route change. See parseHeadEntries.ts.
    const entries = parseHeadEntriesOnce(props.content.headCode, keyPrefix);
    gamDebugLog('head-inject', keyPrefix, { scriptCount: entries.script?.length ?? 0, injected: (entries.script?.length ?? 0) > 0 });
    return entries;
  });
  useHead(headEntries);

  const containerRef = useTemplateRef<HTMLDivElement>('container');

  const desktopHeight = computed(() =>
    props.content.desktopBannerHeight ? `${props.content.desktopBannerHeight}px` : '0px',
  );
  const mobileHeight = computed(() =>
    props.content.mobileBannerHeight ? `${props.content.mobileBannerHeight}px` : '0px',
  );

  let observer: IntersectionObserver | null = null;
  // Set once the ad has entered the viewport; this triggers the body code so the
  // ad is requested lazily, only when scrolled into view.
  let inViewport = false;
  // Guards against running the body code / measurement more than once.
  let adDisplayed = false;
  // Pending requestAnimationFrame handle for the googletag-readiness poll, so it
  // can be cancelled on unmount.
  let gptPollRaf = 0;
  // Caps how long we wait for the head library to define `googletag` before
  // giving up (e.g. when an ad blocker prevents the library from loading).
  const GPT_READY_TIMEOUT_MS = 5000;

  // The bodyCode (googletag.display / cmd.push) depends on the `googletag`
  // global, which the async head library defines only after it has loaded. The
  // head injection and displayAd() can race — the library can lose, throwing
  // "googletag is not defined" — so we wait for the command queue to exist
  // before running the bodyCode.
  // view_promotion measures that the user actually saw the ad. It must fire only
  // after the ad has rendered (postscribe ran), not merely on viewport —
  // otherwise we would count a view that never happened (e.g. the head library
  // was blocked and googletag never loaded, so nothing was displayed).
  function measureView () {
    if (!props.analyticsMeasurable || !props.analyticsBannerInteraction) {
      gamDebugLog('view-skip', gamDebugKey(), { measurable: props.analyticsMeasurable, hasInteraction: !!props.analyticsBannerInteraction });
      return;
    }
    const { $awAnalytics } = useNuxtApp();
    const interaction = AnalyticsBannerInteraction.create({
      ...props.analyticsBannerInteraction,
      promotionName: props.content.campaignName ?? null,
      promotionBannerIndex: 1,
    });
    gamDebugLog('view-measure', gamDebugKey(), { campaignName: props.content.campaignName });
    $awAnalytics[PUSH_VIEW_BANNER]({ analyticsBannerInteraction: interaction });
  }

  function runBodyCode () {
    if (!props.content.bodyCode || !containerRef.value) {
      gamDebugLog('display-skip', gamDebugKey(), { reason: 'no-bodycode-or-container', hasBodyCode: !!props.content.bodyCode, hasContainer: !!containerRef.value });
      return;
    }
    const cleanBodyCode = sanitizeBodyCode(props.content.bodyCode);

    import('postscribe').then((postscribe) => {
      if (containerRef.value) {
        gamDebugLog('postscribe-write', gamDebugKey());
        postscribe.default(containerRef.value, cleanBodyCode);
        gamDebugLog('postscribe-done', gamDebugKey());
        // Snapshot of the registered GAM slots right after this slot displayed.
        try {
          const slots = window.googletag?.pubads?.().getSlots?.() ?? [];
          gamDebugLog('slot-state', gamDebugKey(), { count: slots.length, ids: slots.map(s => s.getSlotElementId?.()) });
        } catch (e) {
          gamDebugLog('slot-state', gamDebugKey(), { error: String(e) });
        }
        // Only now has the ad actually rendered → measure the view.
        measureView();
      }
    }).catch((err) => {
      const { $logger } = useNuxtApp();
      $logger.error(err);
    });
  }

  function whenGptReady (onReady: () => void) {
    const start = performance.now();
    gamDebugLog('gpt-wait', gamDebugKey(), { alreadyReady: !!window.googletag?.cmd });
    const poll = () => {
      if (window.googletag?.cmd) {
        gptPollRaf = 0;
        gamDebugLog('gpt-ready', gamDebugKey(), { waitedMs: Math.round(performance.now() - start) });
        onReady();
        return;
      }
      if (performance.now() - start >= GPT_READY_TIMEOUT_MS) {
        gptPollRaf = 0;
        gamDebugLog('gpt-timeout', gamDebugKey(), { waitedMs: Math.round(performance.now() - start) });
        const { $logger } = useNuxtApp();
        $logger.error(new Error('GAM: googletag did not become available before timeout'));
        return;
      }
      gptPollRaf = requestAnimationFrame(poll);
    };
    poll();
  }

  function displayAd () {
    gamDebugLog('display', gamDebugKey(), { adDisplayed, inViewport });
    if (adDisplayed || !inViewport) {
      gamDebugLog('display-skip', gamDebugKey(), { reason: 'guard', adDisplayed, inViewport });
      return;
    }
    adDisplayed = true;

    if (props.content.bodyCode && containerRef.value) {
      // The library may already be present (hard load / earlier component);
      // whenGptReady runs the bodyCode immediately in that case. The view is
      // measured inside runBodyCode, once the ad has actually rendered.
      whenGptReady(runBodyCode);
    }
  }

  onMounted(() => {
    gamDebugLog('mounted', gamDebugKey(), { hasBodyCode: !!props.content.bodyCode, hasHeadCode: !!props.content.headCode });
    // Report to the GAM SPA plugin so its refresh coordinator can track how many
    // ad components are live (it gates the SPA refresh on the leaving page's ads
    // being gone). See common/utils/awGamSpaRefresh.ts.
    notifyAwGamMount();

    observer = new IntersectionObserver((entries) => {
      for (const entry of entries) {
        if (!entry.isIntersecting) {
          continue;
        }

        gamDebugLog('in-viewport', gamDebugKey());
        observer?.disconnect();
        observer = null;
        inViewport = true;
        displayAd();
      }
    }, { threshold: 0.5 });

    if (containerRef.value) {
      observer.observe(containerRef.value);
    }
  });

  onUnmounted(() => {
    gamDebugLog('unmounted', gamDebugKey());
    observer?.disconnect();
    observer = null;
    if (gptPollRaf) {
      cancelAnimationFrame(gptPollRaf);
      gptPollRaf = 0;
    }
    // Tell the GAM SPA plugin a leaving page's ad slot is gone; it debounces
    // these and runs spaHardReset once the old page's slot divs are cleared, so
    // a slot id reused on the next page refreshes against the new div, not the
    // stale leaving one. See common/utils/awGamSpaRefresh.ts.
    notifyAwGamUnmount();
  });
</script>


<style module lang="scss" rel="stylesheet/scss">
.googleAd {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 100%;
  height: v-bind(mobileHeight);
  background: transparent;

  @include tablet(min) {
    height: v-bind(desktopHeight);
  }
}
</style>
